/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable } from '@angular/core';
import { ConnectorCategory, MigrationConnector } from './migration.models';

/**
 * The connector catalogue — one entry per source system the migration center
 * knows about. This is the registry/strategy point: adding a source means
 * adding a connector here (plus its backend handler in the migration
 * service); the wizard, the connections page and the mapping screens all read
 * the catalogue rather than knowing any connector by name.
 *
 * `availability` is honest on purpose: 'coming-soon' connectors render
 * disabled everywhere — selecting one must never imply it can run.
 */
@Injectable({ providedIn: 'root' })
export class ConnectorRegistry {
  readonly connectors: MigrationConnector[] = [
    // ── A. Alfresco ──────────────────────────────────────────────────────
    {
      id: 'alfresco',
      category: 'alfresco',
      icon: 'sync_alt',
      titleKey: 'DMS.MIGRATION.CONN_ALFRESCO',
      descKey: 'DMS.MIGRATION.CONN_ALFRESCO_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'yes' },
        { key: 'versions', level: 'mode' },
        { key: 'permissions', level: 'mode' },
        { key: 'associations', level: 'mode' },
        { key: 'workflowHistory', level: 'no' },
        { key: 'auditHistory', level: 'limited' },
        { key: 'originalUuid', level: 'mode', noteKey: 'DMS.MIGRATION.CAP_UUID_NOTE' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'serverUrl', labelKey: 'DMS.MIGRATION.F_SERVER_URL', type: 'url', required: true, placeholderKey: 'DMS.MIGRATION.PH_SERVER_URL' },
        {
          key: 'authType',
          labelKey: 'DMS.MIGRATION.F_AUTH_TYPE',
          type: 'select',
          required: true,
          options: [
            { value: 'basic', labelKey: 'DMS.MIGRATION.AUTH_BASIC' },
            { value: 'ticket', labelKey: 'DMS.MIGRATION.AUTH_TICKET' }
          ]
        },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text', required: true },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', required: true, secret: true }
      ]
    },

    // ── B. ECM / DMS ─────────────────────────────────────────────────────
    {
      id: 'sharepoint',
      category: 'ecm',
      icon: 'share',
      titleKey: 'DMS.MIGRATION.CONN_SHAREPOINT',
      descKey: 'DMS.MIGRATION.CONN_SHAREPOINT_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'mode' },
        { key: 'versions', level: 'yes' },
        { key: 'permissions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'siteUrl', labelKey: 'DMS.MIGRATION.F_SITE_URL', type: 'url', required: true, hintKey: 'DMS.MIGRATION.F_SITE_URL_HINT' },
        { key: 'drive', labelKey: 'DMS.MIGRATION.F_DRIVE', type: 'text', hintKey: 'DMS.MIGRATION.F_DRIVE_HINT' },
        { key: 'tenantId', labelKey: 'DMS.MIGRATION.F_TENANT_ID', type: 'text', required: true },
        { key: 'clientId', labelKey: 'DMS.MIGRATION.F_CLIENT_ID', type: 'text', required: true },
        { key: 'clientSecret', labelKey: 'DMS.MIGRATION.F_CLIENT_SECRET', type: 'password', required: true, secret: true }
      ]
    },
    {
      id: 'documentum',
      category: 'ecm',
      icon: 'inventory_2',
      titleKey: 'DMS.MIGRATION.CONN_DOCUMENTUM',
      descKey: 'DMS.MIGRATION.CONN_ECM_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'mode' },
        { key: 'versions', level: 'yes' },
        { key: 'permissions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'baseUrl', labelKey: 'DMS.MIGRATION.F_BASE_URL', type: 'url', required: true, hintKey: 'DMS.MIGRATION.F_DCTM_URL_HINT' },
        { key: 'repository', labelKey: 'DMS.MIGRATION.F_REPOSITORY', type: 'text', required: true },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text', required: true },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', required: true, secret: true },
        { key: 'rootId', labelKey: 'DMS.MIGRATION.F_ROOT_ID', type: 'text', hintKey: 'DMS.MIGRATION.F_ROOT_ID_DCTM_HINT' }
      ]
    },
    {
      id: 'filenet',
      category: 'ecm',
      icon: 'inventory_2',
      titleKey: 'DMS.MIGRATION.CONN_FILENET',
      descKey: 'DMS.MIGRATION.CONN_ECM_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'mode' },
        { key: 'versions', level: 'yes' },
        { key: 'permissions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'baseUrl', labelKey: 'DMS.MIGRATION.F_BASE_URL', type: 'url', required: true, hintKey: 'DMS.MIGRATION.F_FN_URL_HINT' },
        { key: 'objectstore', labelKey: 'DMS.MIGRATION.F_OBJECTSTORE', type: 'text', required: true },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text', required: true },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', required: true, secret: true },
        { key: 'rootPath', labelKey: 'DMS.MIGRATION.F_ROOT_PATH', type: 'text', hintKey: 'DMS.MIGRATION.F_ROOT_PATH_HINT' }
      ]
    },
    {
      id: 'opentext',
      category: 'ecm',
      icon: 'inventory_2',
      titleKey: 'DMS.MIGRATION.CONN_OPENTEXT',
      descKey: 'DMS.MIGRATION.CONN_ECM_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'mode' },
        { key: 'versions', level: 'yes' },
        { key: 'permissions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'baseUrl', labelKey: 'DMS.MIGRATION.F_BASE_URL', type: 'url', required: true, hintKey: 'DMS.MIGRATION.F_BASE_URL_HINT' },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text', required: true },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', required: true, secret: true },
        { key: 'rootId', labelKey: 'DMS.MIGRATION.F_ROOT_ID', type: 'text', hintKey: 'DMS.MIGRATION.F_ROOT_ID_HINT' }
      ]
    },

    // ── C. Legacy systems ────────────────────────────────────────────────
    {
      id: 'lotus-notes',
      category: 'legacy',
      icon: 'history_edu',
      titleKey: 'DMS.MIGRATION.CONN_LOTUS',
      descKey: 'DMS.MIGRATION.CONN_LOTUS_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'mode' },
        { key: 'folders', level: 'no' },
        { key: 'metadata', level: 'mode' },
        { key: 'versions', level: 'no' },
        { key: 'permissions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'baseUrl', labelKey: 'DMS.MIGRATION.F_BASE_URL', type: 'url', required: true, hintKey: 'DMS.MIGRATION.F_KEEP_URL_HINT' },
        { key: 'database', labelKey: 'DMS.MIGRATION.F_DATABASE', type: 'text', required: true, hintKey: 'DMS.MIGRATION.F_NOTES_DB_HINT' },
        { key: 'view', labelKey: 'DMS.MIGRATION.F_VIEW', type: 'text', hintKey: 'DMS.MIGRATION.F_VIEW_HINT' },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text', required: true },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', required: true, secret: true }
      ]
    },
    {
      id: 'database',
      category: 'legacy',
      icon: 'database',
      titleKey: 'DMS.MIGRATION.CONN_DATABASE',
      descKey: 'DMS.MIGRATION.CONN_DATABASE_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'mode' },
        { key: 'metadata', level: 'yes' },
        { key: 'folders', level: 'no' },
        { key: 'versions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        {
          key: 'dbType',
          labelKey: 'DMS.MIGRATION.F_DB_TYPE',
          type: 'select',
          required: true,
          options: [
            { value: 'postgresql', labelKey: 'DMS.MIGRATION.DB_POSTGRESQL' },
            { value: 'mariadb', labelKey: 'DMS.MIGRATION.DB_MARIADB' },
            { value: 'mysql', labelKey: 'DMS.MIGRATION.DB_MYSQL' },
            { value: 'mssql', labelKey: 'DMS.MIGRATION.DB_MSSQL' }
          ]
        },
        { key: 'host', labelKey: 'DMS.MIGRATION.F_HOST', type: 'text', required: true },
        { key: 'port', labelKey: 'DMS.MIGRATION.F_PORT', type: 'number', required: true },
        { key: 'database', labelKey: 'DMS.MIGRATION.F_DATABASE', type: 'text', required: true },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text', required: true },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', required: true, secret: true },
        { key: 'tls', labelKey: 'DMS.MIGRATION.F_TLS', type: 'checkbox' },
        { key: 'query', labelKey: 'DMS.MIGRATION.F_DB_QUERY', type: 'textarea', required: true, hintKey: 'DMS.MIGRATION.F_DB_QUERY_HINT' },
        { key: 'idColumn', labelKey: 'DMS.MIGRATION.F_DB_ID_COL', type: 'text', hintKey: 'DMS.MIGRATION.F_DB_ID_COL_HINT' },
        { key: 'nameColumn', labelKey: 'DMS.MIGRATION.F_DB_NAME_COL', type: 'text', hintKey: 'DMS.MIGRATION.F_DB_NAME_COL_HINT' },
        { key: 'folderColumn', labelKey: 'DMS.MIGRATION.F_DB_FOLDER_COL', type: 'text', hintKey: 'DMS.MIGRATION.F_DB_FOLDER_COL_HINT' },
        { key: 'modifiedColumn', labelKey: 'DMS.MIGRATION.F_DB_MODIFIED_COL', type: 'text' },
        { key: 'fileColumn', labelKey: 'DMS.MIGRATION.F_DB_FILE_COL', type: 'text', hintKey: 'DMS.MIGRATION.F_DB_FILE_COL_HINT' },
        { key: 'basePath', labelKey: 'DMS.MIGRATION.F_DB_BASE_PATH', type: 'text', hintKey: 'DMS.MIGRATION.F_DB_BASE_PATH_HINT' },
        { key: 'blobColumn', labelKey: 'DMS.MIGRATION.F_DB_BLOB_COL', type: 'text', hintKey: 'DMS.MIGRATION.F_DB_BLOB_COL_HINT' }
      ]
    },
    {
      id: 'rest-api',
      category: 'legacy',
      icon: 'api',
      titleKey: 'DMS.MIGRATION.CONN_REST',
      descKey: 'DMS.MIGRATION.CONN_REST_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'mode' },
        { key: 'metadata', level: 'mode' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'endpoint', labelKey: 'DMS.MIGRATION.F_ENDPOINT', type: 'url', required: true, hintKey: 'DMS.MIGRATION.F_ENDPOINT_HINT' },
        {
          key: 'authType',
          labelKey: 'DMS.MIGRATION.F_AUTH_TYPE',
          type: 'select',
          required: true,
          options: [
            { value: 'none', labelKey: 'DMS.MIGRATION.AUTH_NONE' },
            { value: 'basic', labelKey: 'DMS.MIGRATION.AUTH_BASIC' },
            { value: 'bearer', labelKey: 'DMS.MIGRATION.AUTH_BEARER' },
            { value: 'apikey', labelKey: 'DMS.MIGRATION.AUTH_APIKEY' }
          ]
        },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text' },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', secret: true },
        { key: 'token', labelKey: 'DMS.MIGRATION.F_TOKEN', type: 'password', secret: true },
        { key: 'apiKey', labelKey: 'DMS.MIGRATION.F_APIKEY', type: 'password', secret: true },
        { key: 'apiKeyHeader', labelKey: 'DMS.MIGRATION.F_APIKEY_HEADER', type: 'text', hintKey: 'DMS.MIGRATION.F_APIKEY_HEADER_HINT' },
        { key: 'itemsPath', labelKey: 'DMS.MIGRATION.F_ITEMS_PATH', type: 'text', hintKey: 'DMS.MIGRATION.F_ITEMS_PATH_HINT' },
        { key: 'idField', labelKey: 'DMS.MIGRATION.F_ID_FIELD', type: 'text', hintKey: 'DMS.MIGRATION.F_ID_FIELD_HINT' },
        { key: 'nameField', labelKey: 'DMS.MIGRATION.F_NAME_FIELD', type: 'text', hintKey: 'DMS.MIGRATION.F_NAME_FIELD_HINT' },
        { key: 'folderField', labelKey: 'DMS.MIGRATION.F_FOLDER_FIELD', type: 'text', hintKey: 'DMS.MIGRATION.F_FOLDER_FIELD_HINT' },
        { key: 'modifiedField', labelKey: 'DMS.MIGRATION.F_MODIFIED_FIELD', type: 'text' },
        { key: 'sizeField', labelKey: 'DMS.MIGRATION.F_SIZE_FIELD', type: 'text' },
        { key: 'mimeField', labelKey: 'DMS.MIGRATION.F_MIME_FIELD', type: 'text' },
        { key: 'contentField', labelKey: 'DMS.MIGRATION.F_CONTENT_FIELD', type: 'text', hintKey: 'DMS.MIGRATION.F_CONTENT_FIELD_HINT' },
        { key: 'contentUrlTemplate', labelKey: 'DMS.MIGRATION.F_CONTENT_TPL', type: 'text', hintKey: 'DMS.MIGRATION.F_CONTENT_TPL_HINT' }
      ]
    },

    // ── D. File storage ──────────────────────────────────────────────────
    {
      id: 'file-server',
      category: 'file-storage',
      icon: 'folder_shared',
      titleKey: 'DMS.MIGRATION.CONN_FILESERVER',
      descKey: 'DMS.MIGRATION.CONN_FILESERVER_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'limited' },
        { key: 'versions', level: 'no' },
        { key: 'permissions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'path', labelKey: 'DMS.MIGRATION.F_PATH', type: 'text', required: true, hintKey: 'DMS.MIGRATION.F_PATH_HINT' },
        {
          key: 'authType',
          labelKey: 'DMS.MIGRATION.F_AUTH_TYPE',
          type: 'select',
          required: true,
          options: [
            { value: 'none', labelKey: 'DMS.MIGRATION.AUTH_NONE' },
            { value: 'basic', labelKey: 'DMS.MIGRATION.AUTH_BASIC' }
          ]
        },
        { key: 'domain', labelKey: 'DMS.MIGRATION.F_DOMAIN', type: 'text' },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text' },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', secret: true }
      ]
    },
    {
      id: 'sftp',
      category: 'file-storage',
      icon: 'terminal',
      titleKey: 'DMS.MIGRATION.CONN_SFTP',
      descKey: 'DMS.MIGRATION.CONN_SFTP_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'versions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'host', labelKey: 'DMS.MIGRATION.F_HOST', type: 'text', required: true },
        { key: 'port', labelKey: 'DMS.MIGRATION.F_PORT', type: 'number' },
        { key: 'path', labelKey: 'DMS.MIGRATION.F_PATH', type: 'text' },
        { key: 'username', labelKey: 'DMS.MIGRATION.F_USERNAME', type: 'text', required: true },
        { key: 'password', labelKey: 'DMS.MIGRATION.F_SECRET', type: 'password', secret: true }
      ]
    },
    {
      id: 's3',
      category: 'file-storage',
      icon: 'cloud_queue',
      titleKey: 'DMS.MIGRATION.CONN_S3',
      descKey: 'DMS.MIGRATION.CONN_S3_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'limited' },
        { key: 'versions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'endpoint', labelKey: 'DMS.MIGRATION.F_ENDPOINT', type: 'url', required: true },
        { key: 'bucket', labelKey: 'DMS.MIGRATION.F_BUCKET', type: 'text', required: true },
        { key: 'accessKey', labelKey: 'DMS.MIGRATION.F_ACCESS_KEY', type: 'text', required: true },
        { key: 'secretKey', labelKey: 'DMS.MIGRATION.F_SECRET_KEY', type: 'password', required: true, secret: true },
        { key: 'region', labelKey: 'DMS.MIGRATION.F_REGION', type: 'text' },
        { key: 'ssl', labelKey: 'DMS.MIGRATION.F_SSL', type: 'checkbox' }
      ]
    },

    // ── E. Cloud storage ─────────────────────────────────────────────────
    {
      id: 'onedrive',
      category: 'cloud',
      icon: 'cloud',
      titleKey: 'DMS.MIGRATION.CONN_ONEDRIVE',
      descKey: 'DMS.MIGRATION.CONN_CLOUD_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'mode' },
        { key: 'versions', level: 'yes' },
        { key: 'permissions', level: 'no' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'user', labelKey: 'DMS.MIGRATION.F_OD_USER', type: 'text', required: true, hintKey: 'DMS.MIGRATION.F_OD_USER_HINT' },
        { key: 'tenantId', labelKey: 'DMS.MIGRATION.F_TENANT_ID', type: 'text', required: true },
        { key: 'clientId', labelKey: 'DMS.MIGRATION.F_CLIENT_ID', type: 'text', required: true },
        { key: 'clientSecret', labelKey: 'DMS.MIGRATION.F_CLIENT_SECRET', type: 'password', required: true, secret: true }
      ]
    },
    {
      id: 'google-drive',
      category: 'cloud',
      icon: 'cloud',
      titleKey: 'DMS.MIGRATION.CONN_GDRIVE',
      descKey: 'DMS.MIGRATION.CONN_CLOUD_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'mode' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'mode' },
        { key: 'versions', level: 'yes' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        {
          key: 'accessToken',
          labelKey: 'DMS.MIGRATION.F_ACCESS_TOKEN',
          type: 'password',
          secret: true,
          hintKey: 'DMS.MIGRATION.F_ACCESS_TOKEN_HINT'
        },
        { key: 'serviceAccountJson', labelKey: 'DMS.MIGRATION.F_SA_JSON', type: 'textarea', secret: true, hintKey: 'DMS.MIGRATION.F_SA_JSON_HINT' },
        { key: 'folderId', labelKey: 'DMS.MIGRATION.F_FOLDER_ID', type: 'text', hintKey: 'DMS.MIGRATION.F_FOLDER_ID_HINT' },
        { key: 'exportNative', labelKey: 'DMS.MIGRATION.F_EXPORT_NATIVE', type: 'checkbox', hintKey: 'DMS.MIGRATION.F_EXPORT_NATIVE_HINT' }
      ]
    },
    {
      id: 'box',
      category: 'cloud',
      icon: 'cloud',
      titleKey: 'DMS.MIGRATION.CONN_BOX',
      descKey: 'DMS.MIGRATION.CONN_CLOUD_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'mode' },
        { key: 'versions', level: 'yes' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'clientId', labelKey: 'DMS.MIGRATION.F_CLIENT_ID', type: 'text', required: true },
        { key: 'clientSecret', labelKey: 'DMS.MIGRATION.F_CLIENT_SECRET', type: 'password', required: true, secret: true },
        { key: 'folderId', labelKey: 'DMS.MIGRATION.F_FOLDER_ID', type: 'text', hintKey: 'DMS.MIGRATION.F_FOLDER_ID_BOX_HINT' }
      ]
    },

    // ── F. Bulk import ───────────────────────────────────────────────────
    {
      id: 'bulk-folder',
      category: 'bulk',
      icon: 'drive_folder_upload',
      titleKey: 'DMS.MIGRATION.CONN_BULK_FOLDER',
      descKey: 'DMS.MIGRATION.CONN_BULK_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' },
        { key: 'metadata', level: 'limited' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'path', labelKey: 'DMS.MIGRATION.F_PATH', type: 'text', required: true }
      ]
    },
    {
      id: 'bulk-zip',
      category: 'bulk',
      icon: 'folder_zip',
      titleKey: 'DMS.MIGRATION.CONN_BULK_ZIP',
      descKey: 'DMS.MIGRATION.CONN_BULK_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'folders', level: 'yes' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'path', labelKey: 'DMS.MIGRATION.F_PATH', type: 'text', required: true, hintKey: 'DMS.MIGRATION.F_PATH_HINT' }
      ]
    },
    {
      id: 'bulk-csv',
      category: 'bulk',
      icon: 'csv',
      titleKey: 'DMS.MIGRATION.CONN_BULK_CSV',
      descKey: 'DMS.MIGRATION.CONN_BULK_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'metadata', level: 'yes' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'path', labelKey: 'DMS.MIGRATION.F_PATH', type: 'text', required: true, hintKey: 'DMS.MIGRATION.F_PATH_HINT' }
      ]
    },
    {
      id: 'bulk-excel',
      category: 'bulk',
      icon: 'table',
      titleKey: 'DMS.MIGRATION.CONN_BULK_EXCEL',
      descKey: 'DMS.MIGRATION.CONN_BULK_DESC',
      availability: 'available',
      capabilities: [
        { key: 'content', level: 'yes' },
        { key: 'metadata', level: 'yes' }
      ],
      fields: [
        { key: 'name', labelKey: 'DMS.MIGRATION.F_CONN_NAME', type: 'text', required: true },
        { key: 'path', labelKey: 'DMS.MIGRATION.F_PATH', type: 'text', required: true, hintKey: 'DMS.MIGRATION.F_PATH_HINT' },
        { key: 'sheet', labelKey: 'DMS.MIGRATION.F_SHEET', type: 'text', hintKey: 'DMS.MIGRATION.F_SHEET_HINT' }
      ]
    }
  ];

  readonly categories: { id: ConnectorCategory; titleKey: string }[] = [
    { id: 'alfresco', titleKey: 'DMS.MIGRATION.CAT_ALFRESCO' },
    { id: 'ecm', titleKey: 'DMS.MIGRATION.CAT_ECM' },
    { id: 'legacy', titleKey: 'DMS.MIGRATION.CAT_LEGACY' },
    { id: 'file-storage', titleKey: 'DMS.MIGRATION.CAT_FILE_STORAGE' },
    { id: 'cloud', titleKey: 'DMS.MIGRATION.CAT_CLOUD' },
    { id: 'bulk', titleKey: 'DMS.MIGRATION.CAT_BULK' }
  ];

  get(id: string | undefined | null): MigrationConnector | undefined {
    return this.connectors.find((c) => c.id === id);
  }

  byCategory(category: ConnectorCategory): MigrationConnector[] {
    return this.connectors.filter((c) => c.category === category);
  }

  /** Connectors that can actually be selected today. */
  usable(): MigrationConnector[] {
    return this.connectors.filter((c) => c.availability === 'available');
  }
}
